import { useRef, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Flex, Text, Image, chakra } from '@chakra-ui/react';
import { ChevronDown, ChevronRight, Building2, User } from 'lucide-react';
import { useUser } from '@/constants/UserContext';
import { DEFAULT_PROFILE_IMAGE } from '@/constants/defaultImages';

function OrgAvatar({ src, name, size = 24 }: { src: string | null; name: string; size?: number }) {
  return src ? (
    <Image
      src={src}
      alt={name}
      boxSize={`${size}px`}
      borderRadius="full"
      objectFit="cover"
      flexShrink={0}
      onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_PROFILE_IMAGE; }}
    />
  ) : (
    <Box
      boxSize={`${size}px`}
      borderRadius="full"
      bg="volyx.primaryLight"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <Building2 size={size * 0.5} color="#007AFF" />
    </Box>
  );
}

/** Volunteer profile photo in switcher; user silhouette when no image. */
function VolunteerAvatar({ src, name, size = 28 }: { src: string | null | undefined; name: string; size?: number }) {
  return src ? (
    <Image
      src={src}
      alt={name}
      boxSize={`${size}px`}
      borderRadius="full"
      objectFit="cover"
      flexShrink={0}
      onError={(e) => { (e.currentTarget as HTMLImageElement).src = DEFAULT_PROFILE_IMAGE; }}
    />
  ) : (
    <Box
      boxSize={`${size}px`}
      borderRadius="full"
      bg="volyx.primaryLight"
      display="flex"
      alignItems="center"
      justifyContent="center"
      flexShrink={0}
    >
      <User size={size * 0.5} color="#007AFF" strokeWidth={2} />
    </Box>
  );
}

export function OrgContextSwitcher() {
  const navigate = useNavigate();
  const { user, adminOrgs, activeAdminOrgId, setActiveAdminOrgId } = useUser();
  const volunteerLabel = user?.first_name?.trim() || 'Volunteer';
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (adminOrgs.length === 0) return null;

  const activeOrg = adminOrgs.find((o) => o.id === activeAdminOrgId) ?? null;
  const isActive = activeAdminOrgId !== null;
  const isMulti = adminOrgs.length > 1;
  const showDropdown =
    isMulti || (isActive && !isMulti);
  const canToggleFromBanner =
    showDropdown || (!isActive && !isMulti);

  return (
    <Box position="relative" mb="4" ref={dropdownRef}>
      <Flex
        align="center"
        gap="3"
        minH="52px"
        py="2.5"
        px="4"
        bg={isActive ? '#EBF4FF' : 'volyx.lightGray'}
        border="1px solid"
        borderColor={isActive ? 'blue.200' : 'volyx.mediumGray'}
        borderRadius="xl"
        cursor={canToggleFromBanner ? 'pointer' : 'default'}
        userSelect="none"
        onClick={() => {
          if (isMulti || (isActive && !isMulti)) {
            setOpen((v) => !v);
            return;
          }
          if (!isActive && !isMulti) {
            setActiveAdminOrgId(adminOrgs[0].id);
          }
        }}
        transition="all 0.2s"
        _hover={canToggleFromBanner ? { borderColor: 'blue.300' } : undefined}
      >
        {/* Avatar / icon */}
        {activeOrg ? (
          <OrgAvatar src={activeOrg.profileImage} name={activeOrg.name} size={28} />
        ) : (
          <Box
            boxSize="28px"
            borderRadius="full"
            bg="volyx.mediumGray"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexShrink={0}
            color="volyx.textSecondary"
          >
            <Building2 size={14} aria-hidden />
          </Box>
        )}

        {/* Label */}
        <Text
          m="0"
          fontSize={{ base: 'md', sm: 'lg' }}
          fontWeight="semibold"
          color={isActive ? 'blue.700' : 'volyx.textSecondary'}
          flex="1"
          lineClamp={1}
        >
          {isActive && activeOrg
            ? `${activeOrg.name} Admin`
            : 'Enter organization mode'}
        </Text>

        {/* Options indicator: chevron opens menu (multi, or single-org while in org mode) */}
        {(isMulti || (isActive && !isMulti)) && (
          <Box
            color="volyx.textSecondary"
            flexShrink={0}
            style={{
              transition: 'transform 0.2s',
              transform: open ? 'rotate(180deg)' : 'none',
            }}
            aria-hidden
          >
            <ChevronDown size={18} strokeWidth={2.25} />
          </Box>
        )}
        {!isMulti && !isActive && (
          <Box color="volyx.textSecondary" flexShrink={0} aria-hidden>
            <ChevronRight size={18} strokeWidth={2.25} />
          </Box>
        )}

        {/* Single org non-active: show explicit switch button too */}
        {!isActive && !isMulti && (
          <chakra.button
            type="button"
            display="flex"
            alignItems="center"
            justifyContent="center"
            minH="36px"
            px="3"
            border="none"
            bg="#007AFF"
            color="white"
            cursor="pointer"
            borderRadius="lg"
            fontSize="sm"
            fontWeight="semibold"
            _hover={{ opacity: 0.85 }}
            onClick={(e) => {
              e.stopPropagation();
              setActiveAdminOrgId(adminOrgs[0].id);
            }}
          >
            Enter
          </chakra.button>
        )}
      </Flex>

      {/* Dropdown: multi-org always; single-org when in org mode (no X — exit via menu) */}
      {showDropdown && open && (
        <Box
          position="absolute"
          top="calc(100% + 4px)"
          left="0"
          right="0"
          bg="white"
          border="1px solid"
          borderColor="volyx.mediumGray"
          borderRadius="xl"
          boxShadow="0 8px 24px rgba(0,0,0,0.10)"
          zIndex={200}
          overflow="hidden"
        >
          {/* Back to volunteer view — only when currently in org admin mode */}
          {activeAdminOrgId !== null && (
            <>
              <chakra.button
                type="button"
                w="full"
                px="3"
                py="2.5"
                display="flex"
                alignItems="center"
                gap="2.5"
                bg="transparent"
                border="none"
                cursor="pointer"
                textAlign="left"
                _hover={{ bg: 'volyx.lightGray' }}
                onClick={() => {
                  setActiveAdminOrgId(null);
                  setOpen(false);
                  navigate('/organizations');
                }}
              >
                <VolunteerAvatar src={user?.profile_image} name={volunteerLabel} size={28} />
                <Text m="0" fontSize="sm" fontWeight="medium" color="volyx.textSecondary">
                  My volunteer view
                </Text>
              </chakra.button>

              <Box h="1px" bg="volyx.mediumGray" mx="3" />
            </>
          )}

          {/* Org rows */}
          {adminOrgs.map((org) => {
            const selected = org.id === activeAdminOrgId;
            return (
              <chakra.button
                key={org.id}
                type="button"
                w="full"
                px="3"
                py="2.5"
                display="flex"
                alignItems="center"
                gap="2.5"
                bg={selected ? '#EBF4FF' : 'transparent'}
                border="none"
                cursor="pointer"
                textAlign="left"
                _hover={{ bg: selected ? '#EBF4FF' : 'volyx.lightGray' }}
                onClick={() => {
                  setActiveAdminOrgId(org.id);
                  setOpen(false);
                }}
              >
                <OrgAvatar src={org.profileImage} name={org.name} size={28} />
                <Text
                  m="0"
                  fontSize="sm"
                  fontWeight={selected ? 'semibold' : 'medium'}
                  color={selected ? 'blue.700' : 'volyx.primary'}
                  flex="1"
                  lineClamp={1}
                >
                  {org.name}
                </Text>
                {selected && (
                  <Box
                    w="8px"
                    h="8px"
                    borderRadius="full"
                    bg="#007AFF"
                    flexShrink={0}
                  />
                )}
              </chakra.button>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
